import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { toast } from "sonner";

const ENTITY_TYPES = ["Module", "Feature", "Agent", "Connector", "MobileFeature", "Menu", "API"];

export default function EntitlementsPage() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [effective, setEffective] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [form, setForm] = useState({ entityType: "Module", entityCode: "", enabled: true });

  useEffect(() => {
    api.get("/v1/tenants").then((r) => setTenants(r.data.data.items)).catch(() => {});
  }, []);

  const load = async (id) => {
    setTenantId(id);
    if (!id) { setEffective({}); setOverrides([]); return; }
    try {
      const r = await api.get(`/v1/entitlements/tenant/${id}`);
      setEffective(r.data.data.effective);
      setOverrides(r.data.data.overrides);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const toggle = async () => {
    if (!form.entityCode) return toast.error("Enter an entity code");
    try {
      await api.post("/v1/entitlements/toggle", { tenantId, ...form, entityCode: form.entityCode.toUpperCase() });
      toast.success("Entitlement updated");
      setForm({ ...form, entityCode: "" });
      load(tenantId);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="entitlements-page">
      <PageHeader title="Entitlement Management" subtitle="Resolve & override per-tenant feature entitlements" />
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-1">Select Tenant</label>
        <select value={tenantId} onChange={(e) => load(e.target.value)} className="w-full md:w-96 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="ent-tenant-select">
          <option value="">Choose a tenant…</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName} ({t.tenantCode})</option>)}
        </select>
      </div>

      {tenantId && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <h3 className="font-semibold text-slate-800">Effective Entitlements</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(effective).map(([type, codes]) => (
                <div key={type} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid={`eff-${type}`}>
                  <h4 className="font-semibold text-slate-700 mb-2 text-sm">{type}s ({codes.length})</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {codes.map((c) => <Badge key={c} color="teal">{c}</Badge>)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm sticky top-20">
              <h3 className="font-semibold text-slate-800 mb-3">Override Entitlement</h3>
              <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
              <select value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-3" data-testid="ent-type">
                {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <label className="block text-sm font-medium text-slate-700 mb-1">Entity Code</label>
              <input value={form.entityCode} onChange={(e) => setForm({ ...form, entityCode: e.target.value })} placeholder="DIGITAL_TWIN" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-3 font-mono" data-testid="ent-code" />
              <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
              <select value={String(form.enabled)} onChange={(e) => setForm({ ...form, enabled: e.target.value === "true" })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-4" data-testid="ent-state">
                <option value="true">Enable</option><option value="false">Disable</option>
              </select>
              <button onClick={toggle} className="w-full bg-primary text-white rounded-lg py-2 text-sm font-semibold hover:bg-primary/90" data-testid="ent-apply">Apply Override</button>

              {overrides.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Active Overrides</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {overrides.map((o) => (
                      <div key={o.id} className="flex items-center justify-between text-xs">
                        <span className="font-mono">{o.entityType}:{o.entityCode}</span>
                        <Badge color={o.enabled ? "green" : "red"}>{o.enabled ? "ON" : "OFF"}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
